import React, { ChangeEvent, useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import MpTheme from '../../../config/MpTheme'
import { Dialog, DialogActions, DialogContent } from '@mui/material'
import {
  Box,
  Button,
  ClickAwayListener,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material'
import {
  CorporateCodePost,
  createCorporateCode,
  updateCorporateCode,
  deactivateCorporateCode,
} from '../../../redux/corporateCodeService'
import { useAppDispatch } from '../../../redux/storeExports'
import { getAllParentBrokersList, AllParentBrokersSelector } from '../../../redux/brokerService'
import { fonts } from '../../../config/SigninTheme'
import { useNavigate, useParams } from 'react-router-dom'
import { CustomBrokersModal } from './CustomBrokersModal'
import { ArrowDropDown } from '@mui/icons-material'
import { Toast } from '../../toast/toast'

export type CorporateFormProps = {
  submitLabel: string
  onSubmit?: () => void
  corporateCodeToEdit?: CorporateCodePost
}

const ITEM_HEIGHT = 48
const ITEM_PADDING_TOP = 8
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
}

const entitlements_list = [
  'meprism:databroker200',
  'meprism:breach',
  'meprism:ppc',
  'meprism:4screen',
]

const BROKER_COVERAGE_OPTIONS = [
  { value: 'all_brokers', label: 'All Brokers' },
  { value: 'standard_limited_coverage_brokers', label: 'Standard Limited Coverage' },
  { value: 'custom_brokers', label: 'Custom Brokers' },
] as const

export const CorporateCodeForm = ({
  submitLabel,
  onSubmit,
  corporateCodeToEdit,
}: CorporateFormProps) => {
  const { handleSubmit } = useForm()
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const params = useParams()
  const [entitlmentName, setEntitlmentName] = React.useState<string[]>([])
  const [openDeleteModal, setOpenDeleteModal] = useState(false)
  const [openAllowLoginModal, setOpenAllowLoginModal] = useState(false)
  const [openBrokersModal, setOpenBrokersModal] = useState(false)
  const [isCustomBrokersExpanded, setIsCustomBrokersExpanded] = useState(false)
  const [customBrokersError, setCustomBrokersError] = useState('')
  const savedConfiguredBrokersRef = useRef<Record<string, string[]>>({})
  const [formData, setFormData] = useState<CorporateCodePost>({
    active: true,
    code: '',
    description: '',
    entitlements: [],
    owner_buid: '',
    title: '',
    cost_per_employee_cents: 150,
    type: 'default',
    federation_type: null,
    account_type: '',
    is_login_enabled: false,
    admin_limit: 4,
    is_partner: null,
    partner_webhook_url: null,
    broker_config_group: 'all_brokers',
    configured_brokers: {},
  })

  const [urlError, setUrlError] = useState('');
  
  const isValidUrl = (value: string) => {
    if (!value) return true;

    let parsed: URL;
    try {
      parsed = new URL(value);
    } catch {
      return false;
    }

    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
      return false;
    }

    const hostname = parsed.hostname;

    if (!hostname) return false;

    if (hostname === 'localhost') return true;

    const domainRegex =
      /^(?=.{1,253}$)(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/;

    return domainRegex.test(hostname);
  };

  const dataBrokerOptions = AllParentBrokersSelector()

  useEffect(() => {
    if (!dataBrokerOptions || dataBrokerOptions.length === 0) {
      dispatch(getAllParentBrokersList())
    }
  }, [dispatch, dataBrokerOptions])

  const handleWebhookChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleChange(e);
    const value = e.target.value;
    setUrlError(isValidUrl(value) ? '' : 'Please enter a valid URL');
  };

  useEffect(() => {
    if (corporateCodeToEdit) {
      let mappedBrokers = corporateCodeToEdit.configured_brokers || {}

      if (Array.isArray(mappedBrokers)) {
        const allSelectedBrokerNames: string[] = mappedBrokers
        if (allSelectedBrokerNames.length > 0 && dataBrokerOptions && dataBrokerOptions.length > 0) {
          const newConfigured: Record<string, string[]> = {}
          allSelectedBrokerNames.forEach((brokerName: string) => {
            const broker = dataBrokerOptions.find((b) => b.name === brokerName)
            const cat = broker?.category || 'Other'
            if (!newConfigured[cat]) newConfigured[cat] = []
            if (!newConfigured[cat].includes(brokerName)) {
              newConfigured[cat].push(brokerName)
            }
          })
          mappedBrokers = newConfigured
        }
      }
      setFormData({
        ...corporateCodeToEdit,
        type: corporateCodeToEdit?.authentication?.type || 'default',
        federation_type: corporateCodeToEdit?.authentication?.federation_opts?.type || null,
        configured_brokers: mappedBrokers,
      })
      setEntitlmentName(corporateCodeToEdit.entitlements)
    }
  }, [corporateCodeToEdit, dataBrokerOptions])

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    })
  }

  const handleConfirmDeleteUsers = async (deleteFlag: boolean) => {
  setOpenDeleteModal(false)
      if (corporateCodeToEdit) {
        const corp_code = params?.corp_code ?? ''
      await dispatch(
        deactivateCorporateCode({
            corp_code,
          deleteFlag,
          }),
        ).unwrap()
    }
          navigate('/home/manage', { replace: true })
        }

  const handleSwitch = (e: ChangeEvent<HTMLInputElement>): void => {
    const value = e.target.checked

    if (corporateCodeToEdit?.active === true && corporateCodeToEdit?.active !== value) {
      if (corporateCodeToEdit.account_type === 'executive') {
        setOpenDeleteModal(true)
        setFormData({
          ...formData,
          active: value,
        })
        return
      }
      else if(corporateCodeToEdit.account_type === 'standard'){
        setOpenDeleteModal(true)
        setFormData({
          ...formData,
          active: value,
        })
        return
      }
    }
    setFormData({
      ...formData,
      active: value,
    })
  }

  const handleAccountTypeChange = (event: SelectChangeEvent) => {
    setFormData({
      ...formData,
      account_type: event.target.value,
    })
  }


  const handleAuthenticationChange = (event: SelectChangeEvent) => {
    const federated = event.target.value === 'federation'
    setFormData({
      ...formData,
      type: federated ? 'federation' : 'default',
      federation_type: federated ? (formData.federation_type || 'okta') : null,
    })
  }

  const handleSelectChange = (
    event: SelectChangeEvent<typeof entitlmentName>,
  ) => {
    const {
      target: { value },
    } = event
    setEntitlmentName(typeof value === 'string' ? value.split(',') : value)
    let selectedEntitlement = value as string[]
    setFormData({
      ...formData,
      entitlements: selectedEntitlement,
    })
  }

  const transformFormData = (data: any) => {
    const newObject = {
      ...data,
      authentication: {
        type: data.type,
        federation_type: data.type === 'federation' ? data.federation_type : null,
      },
      is_login_enabled: data.account_type === 'standard' ? false : data.is_login_enabled,
    }
    delete newObject['type']
    delete newObject['federation_type']

    if (newObject.broker_config_group !== 'custom_brokers') {
      newObject.configured_brokers = {}
    }

    return newObject
  }

  async function onSubmitData() {
    if (formData.broker_config_group === 'custom_brokers') {
      const allSelected = Object.values(formData.configured_brokers || {}).flat()
      if (allSelected.length === 0) {
        setCustomBrokersError('Please select at least one broker when using custom broker configuration.')
        return
      }
    }
    setCustomBrokersError('')
    try {
      if(formData.partner_webhook_url && urlError){
        Toast.show(
          {
            type: "error",
            text1: "Enter a valid Partner Webhook URL"
          }
        )
        return
      }
      if (corporateCodeToEdit) {
        const corp_code = params?.corp_code ?? ''
        const res = await dispatch(
          updateCorporateCode({
            corp_code,
            formData: transformFormData(formData),
          }),
        )
        if (res.payload) navigate('/home/manage', { replace: true })
      } else {
        const res = await dispatch(
          createCorporateCode(transformFormData(formData)),
        )
        if (res.payload) navigate('/home/manage', { replace: true })
      }
    } catch (error) {
      console.error('An error occurred:', error)
    }
  }

  const handlePartnerSwitch = (e: ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      is_partner: e.target.checked
    }) 
  }

  const handleAllowUserToLoginSwitch = (e: ChangeEvent<HTMLInputElement>): void => {
    if (!formData || formData.account_type !== 'executive') {
      return
    }
    if (corporateCodeToEdit?.is_login_enabled !== !formData.is_login_enabled){
    setOpenAllowLoginModal(true)
  }
    else{
      setFormData({
        ...formData,
        is_login_enabled: !formData.is_login_enabled
      })
    }
  }

  const handleConfitmAllowLogin = () => async () => {
    setOpenAllowLoginModal(false)
    setFormData({
      ...formData,
      is_login_enabled: !formData.is_login_enabled,
    })
    
  }
  
  return (
    <form onSubmit={handleSubmit(onSubmitData)}>
      <Stack
        sx={{
          display: 'flex',
          flexDirection: 'column',
          maxWidth: MpTheme.layouts.widths.md,
          width: '100%',
          mx: 'auto',
        }}>
        <Stack
          spacing={5}
          sx={{
            maxWidth: MpTheme.layouts.widths.md,
            width: '100%',
            mx: 'auto',
          }}>
          
          {/* Code*/}
          <Box>
            <Typography variant={'h2'} sx={{ whiteSpace: 'break-spaces' }}>
              Code
            </Typography>
            <TextField
              name="code"
              placeholder="Code"
              required
              fullWidth
              disabled={!!corporateCodeToEdit}
              value={formData.code}
              InputProps={{ inputMode: 'numeric' }}
              onChange={handleChange}
              sx={{
                height: '38px',
                '& .MuiInput-root': {
                  height: '42px',
                },
                '& .MuiInputLabel-root': {
                  marginBottom: '5px',
                },
              }}
            />
          </Box>
          
          {/* Title*/}
          <Box>
            <Typography variant={'h2'} sx={{ whiteSpace: 'break-spaces' }}>
              Title
            </Typography>
            <TextField
              name="title"
              placeholder="Title"
              required
              fullWidth
              value={formData.title}
              InputProps={{ inputMode: 'numeric' }}
              onChange={handleChange}
              sx={{
                height: '38px',
                '& .MuiInput-root': {
                  height: '42px',
                },
                '& .MuiInputLabel-root': {
                  marginBottom: '5px',
                },
              }}
            />
          </Box>
          
          {/* Description*/}
          <Box>
            <Typography variant={'h2'} sx={{ whiteSpace: 'break-spaces' }}>
              Description
            </Typography>
            <TextField
              name="description"
              placeholder="Description"
              required
              fullWidth
              value={formData.description}
              onChange={handleChange}
              sx={{
                height: '38px',
                '& .MuiInput-root': {
                  height: '40px',
                },
              }}
            />
          </Box>
          
          {/* Cost Per Employee*/}
          <Box>
            <Typography variant={'h2'} sx={{ whiteSpace: 'break-spaces' }}>
              Cost Per Employee
            </Typography>
            <TextField
              name="cost_per_employee_cents"
              placeholder="Max Redemption"
              required
              fullWidth
              onChange={handleChange}
              value={formData.cost_per_employee_cents}
              sx={{
                height: '38px',
                '& .MuiInput-root': {
                  height: '40px',
                },
              }}
            />
          </Box>
          {/* Max Admins */}
          <Box>
            <Typography variant={'h2'} sx={{ whiteSpace: 'break-spaces' }}>
              Admin Limit
            </Typography>
            <TextField
            name="admin_limit"
            placeholder="Admin Limit"
            type="number"
            required
            fullWidth
            value={formData.admin_limit}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              const { name, value } = e.target;

              setFormData({
                ...formData,
                [name]: value ===""? undefined : Number(value),
              });
            }}
            onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
              const allowedKeys = [
                "Backspace",
                "Delete",
                "Tab",
                "Escape",
                "Enter",
                "ArrowLeft",
                "ArrowRight",
                "ArrowUp",
                "ArrowDown",
              ];

              if (allowedKeys.includes(e.key)) {
                return;
              }

              if (!/^\d$/.test(e.key)) {
                e.preventDefault();
              }
            }}

            inputProps={
              {
                min: 0,
                max: 100
              }
            }
             error={
              (formData.admin_limit === null || undefined) ||
              formData.admin_limit > 100
            }
            helperText={
              (formData.admin_limit === null || undefined)
                ? "Admin limit is required"
                : formData.admin_limit > 100
                ? "Admin limit cannot exceed 100" : "Maximum number of Admins allowed for this code"
            }
            sx={{
              height: "38px",
              marginBottom: 5,
              "& .MuiInput-root": {
                height: "40px",
              }, "& .MuiFormHelperText-root":{
                marginLeft: 0
              }
            }}
          />            
          </Box>
          
          {/* Owner Buid*/}
          <Box>
            <Typography variant={'h2'} sx={{ whiteSpace: 'break-spaces' }}>
              Owner BUID
            </Typography>
            <TextField
              name="owner_buid"
              placeholder="Owner BUID"
              disabled={!!corporateCodeToEdit}
              required
              fullWidth
              onChange={handleChange}
              value={formData.owner_buid}
              sx={{
                height: '38px',
                '& .MuiInput-root': {
                  height: '40px',
                },
              }}
            />
          </Box>

          {/* Broker Configuration */}
          <Box>
            <Typography variant={'h2'} sx={{ whiteSpace: 'break-spaces' }}>
              Broker Coverage
            </Typography>
            <FormControl fullWidth>
              <Select
                id="broker-config-select"
                value={formData.broker_config_group || 'all_brokers'}
                onChange={(event: SelectChangeEvent) => {
                  const newGroup = event.target.value as CorporateCodePost['broker_config_group']
                  if (formData.broker_config_group === 'custom_brokers' && newGroup !== 'custom_brokers') {
                    savedConfiguredBrokersRef.current = formData.configured_brokers || {}
                  }
                  setFormData({
                    ...formData,
                    broker_config_group: newGroup,
                    configured_brokers: newGroup === 'custom_brokers'
                      ? (Object.keys(formData.configured_brokers || {}).length > 0
                          ? formData.configured_brokers
                          : savedConfiguredBrokersRef.current)
                      : {},
                  })
                }}
              >
                {BROKER_COVERAGE_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* Custom Brokers Input, only shown when custom_brokers is selected */}
          {formData.broker_config_group === 'custom_brokers' && (() => {
            const MAX_VISIBLE = isCustomBrokersExpanded ? 9999 : 3
            const rawSelectedBrokers = Object.values(formData.configured_brokers || {}).flat() as string[]
            const uniquePrefixes = new Set<string>()
            const allSelectedBrokers = rawSelectedBrokers.filter((brokerName) => {
              const prefix = brokerName.split('_')[0]
              if (uniquePrefixes.has(prefix)) {
                return false
              }
              uniquePrefixes.add(prefix)
              return true
            })
            const totalCount = allSelectedBrokers.length
            const visibleBrokers = allSelectedBrokers.slice(0, MAX_VISIBLE)
            const remainingCount = totalCount - MAX_VISIBLE
            const hasOverflow = totalCount > 3

            return (
            <Box>
              <Typography variant={'h2'} sx={{ whiteSpace: 'break-spaces' }}>
                Custom Broker Names
              </Typography>
              <ClickAwayListener onClickAway={() => setIsCustomBrokersExpanded(false)}>
                <FormControl fullWidth>
                  <Select
                  open={false}
                  onOpen={() => {
                    if (isCustomBrokersExpanded) {
                      setIsCustomBrokersExpanded(false)
                      return
                    }
                    if (!hasOverflow) {
                      setOpenBrokersModal(true)
                      return
                    }
                    setOpenBrokersModal(true)
                  }}
                  displayEmpty
                  value=""
                  IconComponent={(props) => (
                    <ArrowDropDown
                      {...props}
                      style={{ cursor: 'pointer', pointerEvents: 'auto' }}
                      onMouseDown={(e: React.MouseEvent) => {
                        e.stopPropagation();
                        setOpenBrokersModal(true);
                      }}
                    />
                  )}
                  renderValue={() => (
                    <Box sx={{ display: 'flex', flexWrap: isCustomBrokersExpanded ? 'wrap' : 'nowrap', gap: 1, flex: 1, overflow: 'hidden', alignItems: 'center' }}>
                      {totalCount > 0 ? (
                        <>
                          {visibleBrokers.map((brokerName) => {
                            const broker = dataBrokerOptions.find((b) => b.name === brokerName)
                            return (
                              <Box
                                key={brokerName}
                                sx={{
                                  bgcolor: '#F3E8FF',
                                  color: '#3A155A',
                                  px: 2,
                                  borderRadius: '16px',
                                  fontSize: '0.78rem',
                                  fontWeight: 500,
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {broker?.display_name || brokerName}
                              </Box>
                            )
                          })}
                          {remainingCount > 0 && (
                            <Box
                              data-role="expand-badge"
                              onMouseDown={(e: React.MouseEvent) => {
                                e.stopPropagation()
                                e.preventDefault()
                                setIsCustomBrokersExpanded(true)
                              }}
                              sx={{
                                bgcolor: '#3A155A',
                                color: '#fff',
                                px: 1.5,
                                borderRadius: '16px',
                                fontSize: '0.75rem',
                                fontWeight: 600,
                                whiteSpace: 'nowrap',
                                cursor: 'pointer',
                                '&:hover': {
                                  bgcolor: '#5A2D8A',
                                },
                                transition: 'background-color 0.15s ease',
                              }}
                            >
                              +{remainingCount}
                            </Box>
                          )}
                        </>
                      ) : (
                        <span style={{ color: 'rgba(0, 0, 0, 0.42)' }}>
                          Click to select brokers
                        </span>
                      )}
                    </Box>
                  )}
                >
                  <MenuItem value="" sx={{ display: 'none' }}></MenuItem>
                  </Select>
                </FormControl>
              </ClickAwayListener>

              <CustomBrokersModal
                open={openBrokersModal}
                onClose={() => setOpenBrokersModal(false)}
                dataBrokerOptions={dataBrokerOptions}
                configuredBrokers={formData.configured_brokers || {}}
                onChange={(newConfigured) => {
                  setCustomBrokersError('')
                  setFormData({
                    ...formData,
                    configured_brokers: newConfigured,
                  })
                }}
              />
              {customBrokersError && (
                <FormHelperText error sx={{ ml: 0 }}>
                  {customBrokersError}
                </FormHelperText>
              )}
            </Box>
            )
          })()}

          {/* Entitlements*/}
          <Box>
            <FormControl sx={{ m: 1, width: '100%' }}>
              <InputLabel id="demo-multiple-name-label">Entitlement</InputLabel>
              <Select
                labelId="demo-multiple-name-label"
                id="demo-multiple-name"
                multiple
                value={entitlmentName}
                onChange={handleSelectChange}
                required
                input={<OutlinedInput label="Name" />}
                MenuProps={MenuProps}>
                {entitlements_list.map((name) => (
                  <MenuItem key={name} value={name}>
                    {name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          
           {/* Account Type*/}
          <Box>
            <FormControl fullWidth>
              <InputLabel id="demo-simple-select-label">
                Account Type
              </InputLabel>
              <Select
                labelId="demo-simple-select-label"
                id="demo-simple-select"
                value={formData.account_type ? formData.account_type : ""}
                label="Account Type"
                onChange={handleAccountTypeChange}
              >
                <MenuItem value="executive">Executive</MenuItem>
                <MenuItem value="standard">Standard</MenuItem>
              </Select>

            </FormControl>
          </Box>
          
          {/* allow_user_to_login switch */}
          {formData && formData.account_type === 'executive' && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}>
            <Typography variant={'subheader'}>Enable Login</Typography>
            <Switch checked={!!formData.is_login_enabled} onChange={handleAllowUserToLoginSwitch} />
          </Box>
          )}
          
          {/* Status switch*/}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}>
            <Typography variant={'subheader'}>Status</Typography>
            <Switch checked={formData.active} onChange={handleSwitch} />
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}>
            <Typography variant={'subheader'}>Enable Partner</Typography>
            <Switch checked={Boolean(formData.is_partner)} onChange={handlePartnerSwitch} />
          </Box>

          <Box>
            <Typography variant={'h2'} sx={{ whiteSpace: 'break-spaces' }}>
              Partner Webhook URL
            </Typography>
            <TextField
              name="partner_webhook_url"
              placeholder="Add URL"
              fullWidth
              disabled={!formData.is_partner}
              required={Boolean(formData.is_partner)}
              value={formData.partner_webhook_url}
              onChange={handleWebhookChange}
              error={!!urlError}
              helperText={urlError}
              sx={{
                height: '38px',
                marginBottom: '20px',
                '& .MuiInput-root': {
                  height: '42px',
                },
                '& .MuiInputLabel-root': {
                  marginBottom: '5px',
                },
              }}
            />
          </Box>
          
          {/* Authentication Type*/}
          <Box sx={{mt: '10px' }}>
            <FormControl fullWidth>
              <InputLabel>Authentication Type</InputLabel>
              <Select
                value={formData.type}
                label="Authentication Type"
                onChange={handleAuthenticationChange}>
                <MenuItem value="default">default</MenuItem>
                <MenuItem value="federation">federation</MenuItem>
              </Select>
            </FormControl>
            <Box sx={{ mt: '15px' }}>
              {formData?.type === 'federation' && (
                <>
                  <InputLabel sx={{ mt: '15px' }}>Federation Type</InputLabel>
                  <Select
                    value={formData.federation_type || 'okta'}
                    label="Federation Type"
                    onChange={(e: SelectChangeEvent) =>
                      setFormData({ ...formData, federation_type: e.target.value })
                    }>
                    <MenuItem value="okta">Okta</MenuItem>
                    <MenuItem value="entraid">Microsoft Entra ID</MenuItem>
                  </Select>
                  <InputLabel sx={{ mt: '15px' }}>Federation Name</InputLabel>
                  <TextField
                    value={`${formData.federation_type === 'entraid' ? 'EntraID' : 'Okta'}-${formData.code}`.toLowerCase().replace(/_/g, '-')}
                    disabled
                    fullWidth
                  />
                </>
              )}
            </Box>
            <Dialog open={openDeleteModal} onClose={() => setOpenDeleteModal(false)}>
              <DialogContent>
                <Typography>
                  {corporateCodeToEdit?.account_type === 'executive' ? (
                    <Typography>
                      Are you sure you want to deactivate the corporate code{' '}
                      <strong>{corporateCodeToEdit?.code}</strong>?
                    </Typography>
                  ) : (
                    <Typography>
                      Do you want to delete all users’ data which belongs to{' '}
                      <strong>{corporateCodeToEdit?.code}</strong>?
                    </Typography>
                  )}
                </Typography>
              </DialogContent>
             
              <DialogActions>
                {corporateCodeToEdit?.account_type === 'executive' ? (
                  <>
                    <Button onClick={() => setOpenDeleteModal(false)}>Cancel</Button>
                    <Button color="error" onClick={() => handleConfirmDeleteUsers(true)}>
                      Deactivate
                    </Button>
                  </>
                ) : (
                  <>
                <Button onClick={() => handleConfirmDeleteUsers(false)}>Keep Data</Button>
                <Button color="error" onClick={() => handleConfirmDeleteUsers(true)}>
                  Delete Data
                </Button>
                  </>
                )}
              </DialogActions>
            </Dialog>

            {/* Allow Login Modal */}
            <Dialog open={openAllowLoginModal} onClose={() => setOpenAllowLoginModal(false)}>
              <DialogContent>
                <Typography>
                  Are you sure you want to {corporateCodeToEdit?.is_login_enabled ? 'disallow' : 'allow'} users to login with the corporate code{' '}
                  <strong>{corporateCodeToEdit?.code}</strong>?
                </Typography>
              </DialogContent>
             
              <DialogActions>
                <Button color="inherit" onClick={() => setOpenAllowLoginModal(false)}>
                  Cancel
                </Button>
                <Button color="primary" onClick={handleConfitmAllowLogin()}>
                  Confirm
                </Button>
              </DialogActions>
            </Dialog>
          </Box>
        </Stack>
        <Box sx={{ mt: 5 }}>
          <Button
            variant={'contained'}
            type={'submit'}
            color={'darkPurple'}
            sx={{
              background: '#426BF0',
              width: '100%',
              '&.Mui-disabled': {
                background: '#426BF0',
                fontFamily: fonts.Inter.SemiBold,
                color: '#ffffff',
              },
            }}>
            {submitLabel}
          </Button>
        </Box>
      </Stack>
    </form>
  )
}